type IdLike = number | string

export function assertPopulated<T>(
  value: T | IdLike | null | undefined
): asserts value is Exclude<T, IdLike> | null | undefined {
  // allow "no relation"
  if (value === null || value === undefined) return

  // disallow raw IDs
  if (typeof value === 'number' || typeof value === 'string') {
    throw new Error('Expected populated relation but got an ID')
  }

  // if it's not null/undefined and not an ID, we just return
  // and the assertion narrows the type for the caller
}

export function assertPopulatedArray<T>(
  value: (T | IdLike | null | undefined)[] | null | undefined
): asserts value is
  | (Exclude<T, IdLike> | null | undefined)[]
  | null
  | undefined {
  if (value == null || value == undefined) return

  for (const v of value) {
    if (v == null) continue
    if (typeof v === 'string' || typeof v === 'number') {
      throw new Error('Expected populated relation but got an ID in array')
    }
  }
}

/**
 * Recursively transforms a Payload type to remove ID unions from relationships.
 *
 * Payload generates types like `staticPage?: (number | null) | StaticPage`
 * This type transforms them to `staticPage?: StaticPage | null`
 *
 * Also handles arrays like `subEntries?: (number | NavBarEntry)[]`
 * transforming them to `subEntries?: NavBarEntry[]`
 *
 * Works recursively through nested objects and arrays.
 */
export type DeepPopulated<T> = T extends null | undefined
  ? T
  : T extends (infer U)[]
    ? DeepPopulateField<U>[] // Strip IDs from array elements too
    : T extends object
      ? { [K in keyof T]: DeepPopulateField<T[K]> }
      : T

type DeepPopulateField<T> =
  // Only strip number/string if T contains both IdLike AND some object type (relationship pattern)
  // Pure number fields like `id: number` or `position: number` should remain unchanged
  number extends T
    ? Extract<T, object> extends never
      ? T // No object type in union → pure number field, keep as-is
      : DeepPopulated<Exclude<T, IdLike>>
    : DeepPopulated<T>

/**
 * Recursively asserts that all relationships in a Payload object are populated
 * (not just IDs) and narrows the type to remove ID unions from relationships.
 *
 * @example
 * ```ts
 * const page = await payload.findByID({ collection: 'staticPages', id: 1, depth: 2 })
 * assertPopulatedDeep(page)
 * // page.someRelation is now typed as SomeType | null instead of (number | null) | SomeType
 * ```
 *
 * @throws Error if any relationship field contains an ID instead of a populated object
 */
export function assertPopulatedDeep<T>(
  value: T
): asserts value is T & DeepPopulated<T> {
  assertPopulatedDeepImpl(value, [])
}

function assertPopulatedDeepImpl(value: unknown, path: string[]): void {
  if (value === null || value === undefined) return

  // If the value is a primitive ID, this is an unpopulated relationship
  if (typeof value === 'number' || typeof value === 'string') {
    // Only throw if we're not at the root (root primitives are checked by caller)
    // and this looks like it could be a relationship ID (i.e., we got here via object traversal)
    if (path.length > 0) {
      // Skip - we can't distinguish between unpopulated relation IDs and legitimate primitive fields
      // like `year: 2024` or `title: "Hello"` at runtime without schema info.
      // The type transformation still works, this just won't catch nested unpopulated relations.
      return
    }
    throw new Error(`Expected populated relation but got an ID: ${value}`)
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      // For array items, check if they're objects (populated) or IDs (unpopulated)
      if (typeof item === 'number') {
        throw new Error(
          `Expected populated relation at ${[...path, `[${index}]`].join('.')} but got an ID: ${item}`
        )
      }
      if (item !== null && item !== undefined && typeof item === 'object') {
        assertPopulatedDeepImpl(item, [...path, `[${index}]`])
      }
    })
    return
  }

  if (typeof value === 'object') {
    for (const [key, val] of Object.entries(value)) {
      // For object properties, recurse into nested objects
      // We check arrays and objects, but skip primitives to avoid false positives
      if (val !== null && val !== undefined) {
        if (typeof val === 'object') {
          assertPopulatedDeepImpl(val, [...path, key])
        }
        // Skip primitive checks - can't distinguish relationship IDs from data fields
      }
    }
  }
}
